import * as React from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { WORKFORCE_PERSONAS } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "./personaTheme";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Send,
  Sparkles,
  CheckCircle2,
  XCircle,
  ShieldAlert,
  Clock,
  Loader2,
  FileText,
  MessageSquare,
  Building2,
  ExternalLink,
  Bot,
  User,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Pane2Props {
  activeThread: any;
  messages: any[];
  onSendMessage: (content: string) => Promise<void>;
  onSelectArtifact?: (artifactId: string) => void;
  onApprove?: (approvalId: string) => Promise<void>;
  onReject?: (approvalId: string, reason?: string) => Promise<void>;
  isLoadingTurn: boolean;
}

/**
 * Interactive Inline Work Card with Embedded Approvals
 */
function InlineMessageArtifact({
  artifactId,
  onSelectArtifact,
  onApprove,
  onReject,
}: {
  artifactId: string;
  onSelectArtifact?: (artifactId: string) => void;
  onApprove?: (approvalId: string) => Promise<void>;
  onReject?: (approvalId: string, reason?: string) => Promise<void>;
}) {
  const artifact = useQuery(api.aiWorkforce.artifacts.getArtifact, { id: artifactId as any });
  const approval = useQuery(api.aiWorkforce.approvals.getApprovalByArtifact, { artifactId: artifactId as any });
  const [isProcessing, setIsProcessing] = React.useState(false);

  if (!artifact) return null;

  const isPending = artifact.status === "pending_approval" || approval?.status === "pending";
  const isApproved = artifact.status === "approved" || approval?.status === "approved";
  const isRejected = artifact.status === "rejected" || approval?.status === "rejected";

  const handleApprove = async () => {
    if (!approval?._id || !onApprove) return;
    setIsProcessing(true);
    try {
      await onApprove(approval._id);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!approval?._id || !onReject) return;
    setIsProcessing(true);
    try {
      await onReject(approval._id);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-gray-200 space-y-3 select-none">
      {/* Work Header Banner */}
      <div className="flex items-center justify-between gap-3 p-3.5 rounded-xl bg-gray-50 border border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0 shadow-2xs">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <span className="font-bold text-gray-900 block truncate text-base">{artifact.title}</span>
            <span className="text-xs text-gray-500 font-medium">Work Draft</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {isApproved ? (
            <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Approved
            </span>
          ) : isRejected ? (
            <span className="px-3 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-xs font-semibold flex items-center gap-1.5">
              <XCircle className="w-4 h-4 text-rose-600" /> Declined
            </span>
          ) : isPending ? (
            <span className="px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold flex items-center gap-1.5 animate-pulse">
              <ShieldAlert className="w-4 h-4 text-amber-600" /> Review Needed
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold">
              Draft
            </span>
          )}

          {onSelectArtifact && (
            <button
              onClick={() => onSelectArtifact(artifactId)}
              className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-700 transition-colors"
              title="Open in Work Panel"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Structured Content Details */}
      {artifact.type === "lead_batch_preview" && artifact.data && (
        <div className="p-4 rounded-xl bg-white border border-gray-200 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
              <span className="text-gray-500 block text-xs font-bold uppercase tracking-wider">Verified Leads</span>
              <span className="font-bold text-gray-900 text-base sm:text-lg mt-0.5 block">{artifact.data.totalFound || artifact.data.leads?.length || 0} Facilities</span>
            </div>
            <div className="p-3 rounded-xl bg-emerald-50/60 border border-emerald-200">
              <span className="text-emerald-700 block text-xs font-bold uppercase tracking-wider">Est. Pipeline</span>
              <span className="font-bold text-emerald-800 text-base sm:text-lg mt-0.5 block">{artifact.data.estPipelineValue || "$0"}</span>
            </div>
          </div>

          {artifact.data.leads && (
            <div className="space-y-2">
              <span className="text-xs uppercase font-bold text-gray-500 tracking-wider block">Prospects:</span>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {artifact.data.leads.map((lead: any, idx: number) => (
                  <div key={idx} className="p-3 rounded-xl bg-gray-50 text-sm flex items-center justify-between gap-3 border border-gray-200">
                    <span className="font-semibold text-gray-900 truncate text-sm">{lead.name}</span>
                    <span className="text-gray-600 text-sm truncate">{lead.contact || lead.contactName || lead.phone || ""}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {artifact.type === "work_order_draft" && artifact.data && (
        <div className="p-4 rounded-xl bg-white border border-gray-200 space-y-2.5 text-sm">
          {artifact.data.siteName && (
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <span className="text-gray-500 text-sm">Site Location:</span>
              <span className="font-bold text-gray-900 text-sm">{artifact.data.siteName}</span>
            </div>
          )}
          {artifact.data.scheduledTime && (
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <span className="text-gray-500 text-sm">Shift Schedule:</span>
              <span className="font-semibold text-gray-800 text-sm">{artifact.data.scheduledTime}</span>
            </div>
          )}
          {artifact.data.standbyStaff && (
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <span className="text-gray-500 text-sm">Assigned Cleaner:</span>
              <span className="font-bold text-blue-700 text-sm">{artifact.data.standbyStaff}</span>
            </div>
          )}
          {artifact.data.reason && (
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">Reason:</span>
              <span className="text-gray-700 text-sm italic">{artifact.data.reason}</span>
            </div>
          )}
        </div>
      )}

      {artifact.type === "invoice_preview" && artifact.data && (
        <div className="p-4 rounded-xl bg-white border border-gray-200 space-y-3 text-sm">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Invoice #{artifact.data.invoiceNumber || "Draft"}</span>
            <span className="text-lg font-bold text-emerald-700">{artifact.data.totalAmount || artifact.data.amountDue || "$0.00"}</span>
          </div>
          {artifact.data.clientName && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Client:</span>
              <span className="font-semibold text-gray-900">{artifact.data.clientName}</span>
            </div>
          )}
          {artifact.data.dueDate && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Due Date:</span>
              <span className="text-gray-700">{artifact.data.dueDate}</span>
            </div>
          )}
        </div>
      )}

      {artifact.type === "hr_onboarding_packet" && artifact.data && (
        <div className="p-4 rounded-xl bg-white border border-gray-200 space-y-3 text-sm">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
            <span className="font-bold text-gray-900 text-base">{artifact.data.candidateName || artifact.data.employeeName || "Candidate Packet"}</span>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs font-semibold">Onboarding</Badge>
          </div>
          {artifact.data.role && (
            <div className="text-sm text-gray-600">Position: <span className="font-semibold text-gray-900">{artifact.data.role}</span></div>
          )}
          {Array.isArray(artifact.data.checklist) && (
            <div className="space-y-1.5 pt-1">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider block">Requirements Checklist:</span>
              {artifact.data.checklist.map((item: string, i: number) => (
                <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {artifact.type === "client_message_draft" && artifact.data && (
        <div className="p-4 rounded-xl bg-white border border-gray-200 space-y-2.5 text-sm">
          <div className="border-b border-gray-100 pb-2">
            <span className="text-xs text-gray-500 block">Recipient: {artifact.data.recipient || "Client"}</span>
            <span className="font-bold text-gray-900 text-base">{artifact.data.subject || "Client Outreach Notice"}</span>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{artifact.data.body || artifact.data.message}</p>
        </div>
      )}

      {/* Inline Approval Action Buttons */}
      {isPending && approval && (
        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleApprove}
            disabled={isProcessing}
            className="flex-1 py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-sm transition-all disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span>Approve</span>
          </button>
          <button
            onClick={handleReject}
            disabled={isProcessing}
            className="py-2.5 px-4 rounded-xl bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 font-semibold text-sm flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <XCircle className="w-4 h-4 text-rose-500" />
            <span>Decline</span>
          </button>
        </div>
      )}
    </div>
  );
}

const CHANNEL_DESCRIPTIONS: Record<string, string> = {
  "lead-generation": "Scout commercial buildings, identify property managers, and build pipeline leads.",
  "daily-dispatch": "Coordinate cleaning shifts, dispatch standby cleaners, and monitor site check-ins.",
  "client-escalations": "Resolve client feedback tickets, track quality ratings, and manage contract renewals.",
  "hr-and-onboarding": "Onboard new cleaners, track OSHA safety certifications, and manage employee documents.",
  "billing-sync": "Reconcile GPS check-ins against timecards, audit extra-service costs, and prepare invoice drafts.",
};

export function Pane2CollaborationThread({
  activeThread,
  messages,
  onSendMessage,
  onSelectArtifact,
  onApprove,
  onReject,
  isLoadingTurn,
}: Pane2Props) {
  const [inputContent, setInputContent] = React.useState("");
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoadingTurn]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputContent.trim() || isLoadingTurn) return;
    const text = inputContent.trim();
    setInputContent("");
    await onSendMessage(text);
  };

  const isDM = activeThread?.type === "dm" || activeThread?.participantPersonas?.length === 1;
  const singlePersona =
    isDM && activeThread?.participantPersonas?.[0]
      ? WORKFORCE_PERSONAS[activeThread.participantPersonas[0]]
      : null;

  const threadSubtitle = singlePersona
    ? singlePersona.description
    : (activeThread?.channelSlug && CHANNEL_DESCRIPTIONS[activeThread.channelSlug]) ||
      "Operations collaboration channel.";

  const renderStatusBadge = (state?: string, isStreaming?: boolean) => {
    if (isStreaming) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 animate-pulse">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting...
        </span>
      );
    }

    switch (state) {
      case "received":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
            Received request...
          </span>
        );
      case "querying_db":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700">
            <Clock className="w-3.5 h-3.5 text-amber-500" /> Checking records...
          </span>
        );
      case "composio_executing":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-700">
            <Sparkles className="w-3.5 h-3.5 text-purple-500" /> Coordinating apps...
          </span>
        );
      case "generating_artifact":
        return (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700">
            <FileText className="w-3.5 h-3.5 text-blue-500" /> Preparing work draft...
          </span>
        );
      case "done":
      default:
        return null;
    }
  };

  return (
    <div className="w-full h-full min-h-0 min-w-0 bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
      {/* Thread Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-white min-w-0 flex-shrink-0">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shadow-xs flex-shrink-0">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 truncate">
              <span className="truncate">{activeThread?.title || "Operations Channel"}</span>
              <Badge
                variant="outline"
                className={`text-xs font-semibold ${
                  isDM
                    ? "text-indigo-700 bg-indigo-50 border-indigo-200"
                    : "text-blue-700 bg-blue-50 border-blue-200"
                }`}
              >
                {isDM ? "1:1 Chat" : "Team Channel"}
              </Badge>
            </h2>
            <p className="text-sm text-gray-500 font-medium truncate mt-0.5" title={threadSubtitle}>
              {threadSubtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Message Feed */}
      <div className="flex-1 overflow-y-auto p-5 space-y-5 custom-scrollbar bg-slate-50/40 min-w-0">
        {messages.map((msg, idx) => {
          const isUser = msg.senderType === "user";
          const persona = msg.senderPersona ? WORKFORCE_PERSONAS[msg.senderPersona] : null;
          const theme = getPersonaTheme(msg.senderPersona);
          const statusBadge = renderStatusBadge(msg.telemetryState, msg.isStreaming);

          // Show subtle divider when transitioning from compacted audit archive to active working set
          const prevMsg = idx > 0 ? messages[idx - 1] : null;
          const isFirstUncompacted =
            prevMsg && prevMsg.isCompacted === true && msg.isCompacted !== true;

          return (
            <React.Fragment key={msg._id}>
              {isFirstUncompacted && (
                <div className="flex items-center justify-center my-4 gap-3 select-none">
                  <div className="h-px bg-slate-200 flex-1" />
                  <span className="text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-3 py-1 rounded-full flex items-center gap-1.5 shadow-2xs">
                    <Sparkles className="w-3.5 h-3.5 text-blue-500" />
                    Prior Context Consolidated into Durable Memory
                  </span>
                  <div className="h-px bg-slate-200 flex-1" />
                </div>
              )}
              <div
                className={`flex items-start gap-3.5 min-w-0 ${
                  isUser ? "flex-row-reverse" : "flex-row"
                }`}
              >
              {/* Avatar */}
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-xs ${
                  isUser
                    ? "bg-blue-600 text-white"
                    : `${theme.bgLight} border ${theme.border} ${theme.text}`
                }`}
              >
                {isUser ? <User className="w-5 h-5 text-white" /> : <Bot className="w-5 h-5" />}
              </div>

              {/* Message Card Container */}
              <div
                className={`max-w-3xl min-w-0 rounded-2xl p-4 sm:p-5 space-y-3 break-words ${
                  isUser
                    ? "bg-blue-600 text-white rounded-tr-none shadow-md shadow-blue-600/10"
                    : "bg-white border border-gray-200 text-gray-900 rounded-tl-none shadow-sm"
                }`}
              >
                {/* Agent Header */}
                {!isUser && (
                  <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2.5">
                    <div className="flex items-center gap-2 min-w-0 truncate">
                      <span className="text-base font-bold text-gray-900 truncate">
                        {persona?.name || "Operations Assistant"}
                      </span>
                      <span className="text-sm text-gray-500 font-medium truncate">
                        ({persona?.title || "Specialist"})
                      </span>
                    </div>
                    {statusBadge}
                  </div>
                )}

                {/* Body Text with Rich Markdown and Real-Time Typing Cursor */}
                <div className={`text-base leading-relaxed font-normal break-words ${isUser ? "text-white" : "text-gray-800"}`}>
                  {isUser ? (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  ) : (
                    <div className="space-y-2.5">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ node, ...props }) => <h1 className="text-lg font-bold text-gray-900 mt-3 mb-1.5" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-base font-bold text-gray-900 mt-2.5 mb-1" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-sm font-bold text-gray-800 mt-2 mb-1" {...props} />,
                          p: ({ node, ...props }) => <p className="text-base text-gray-800 leading-relaxed mb-2 last:mb-0" {...props} />,
                          ul: ({ node, ...props }) => <ul className="list-disc list-outside pl-5 space-y-1 mb-2 text-base text-gray-800" {...props} />,
                          ol: ({ node, ...props }) => <ol className="list-decimal list-outside pl-5 space-y-1 mb-2 text-base text-gray-800" {...props} />,
                          li: ({ node, ...props }) => <li className="text-base text-gray-800 leading-relaxed" {...props} />,
                          strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
                          hr: ({ node, ...props }) => <hr className="my-3 border-gray-200" {...props} />,
                          blockquote: ({ node, ...props }) => (
                            <blockquote className="border-l-4 border-blue-500 pl-3.5 py-1.5 my-2 bg-blue-50/60 rounded-r text-gray-700 text-sm italic" {...props} />
                          ),
                          code: ({ node, inline, className, children, ...props }: any) => {
                            if (inline) {
                              return (
                                <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-sm font-mono border border-gray-200" {...props}>
                                  {children}
                                </code>
                              );
                            }
                            return (
                              <div className="my-2 rounded-xl bg-gray-900 text-gray-100 p-3.5 text-sm font-mono overflow-x-auto border border-gray-800">
                                <code {...props}>{children}</code>
                              </div>
                            );
                          },
                          table: ({ node, ...props }) => (
                            <div className="my-2 overflow-x-auto rounded-xl border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200 text-sm text-left" {...props} />
                            </div>
                          ),
                          th: ({ node, ...props }) => <th className="bg-gray-50 px-3.5 py-2 font-semibold text-gray-700 text-sm" {...props} />,
                          td: ({ node, ...props }) => <td className="px-3.5 py-2 border-t border-gray-100 text-gray-800 text-sm" {...props} />,
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                      {msg.isStreaming && (
                        <span className="inline-block w-2.5 h-4.5 ml-1 bg-blue-600 animate-pulse rounded-xs align-middle" />
                      )}
                    </div>
                  )}
                </div>

                {/* Structured Step Checklist */}
                {msg.stepChecklist && msg.stepChecklist.length > 0 && (
                  <div className="mt-3.5 p-3.5 rounded-xl bg-gray-50 border border-gray-200 space-y-2">
                    <div className="text-xs uppercase font-bold text-gray-500 tracking-wider">
                      Task Progress
                    </div>
                    {msg.stepChecklist.map((item: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2.5 text-gray-700 font-medium text-sm">
                        {item.status === "done" ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        ) : item.status === "running" ? (
                          <Loader2 className="w-4 h-4 text-amber-600 animate-spin flex-shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <span
                          className={
                            item.status === "done"
                              ? "text-gray-800"
                              : item.status === "running"
                              ? "text-amber-700 font-bold"
                              : "text-gray-400"
                          }
                        >
                          {item.step}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Interactive Inline Work Card */}
                {msg.artifactId && (
                  <InlineMessageArtifact
                    artifactId={msg.artifactId}
                    onSelectArtifact={onSelectArtifact}
                    onApprove={onApprove}
                    onReject={onReject}
                  />
                )}
              </div>
            </div>
          </React.Fragment>
        );
      })}

        {isLoadingTurn && (
          <div className="flex items-center gap-3 text-gray-700 text-sm py-3 px-4 rounded-2xl bg-white border border-gray-200 shadow-sm w-fit">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            <span className="font-semibold">Coordinating your request...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <div className="p-4 border-t border-gray-200 bg-white min-w-0 flex-shrink-0">
        <form onSubmit={handleSubmit} className="flex items-end gap-3 min-w-0">
          <textarea
            value={inputContent}
            onChange={(e) => setInputContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder="Ask a question or assign a task (e.g., @Hunter scout commercial office buildings in Dallas)..."
            rows={2}
            className="flex-1 min-w-0 bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 text-sm sm:text-base text-gray-900 placeholder-gray-400 focus:bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 resize-none transition-all shadow-xs"
          />
          <button
            type="submit"
            disabled={!inputContent.trim() || isLoadingTurn}
            className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-sm flex-shrink-0"
          >
            <Send className="w-4 h-4" />
            <span>Send</span>
          </button>
        </form>
      </div>
    </div>
  );
}
